import {
  Boxes,
  Cpu,
  Flame,
  Gauge,
  Headphones,
  HeartPulse,
  LayoutGrid,
  Link2,
  Network,
  Radio,
  ShieldCheck,
  SquareTerminal,
  Users,
  Wrench,
} from 'lucide-react'
import type { NavItem } from './config'

/**
 * The two Wi-Fi hardware nav groups, shared by both the old-UI
 * (`old-nav-config`) and new-UI (`config`) nav trees so they never diverge.
 *
 * A venue sees exactly ONE of these below the captive-portal group, chosen by
 * `ctx.hardwareTier` (see `docs/wifi-hardware-simplification/`). Both are
 * venue-scoped and render as one-level accordions — their children are the
 * former SnapGuard tabs / hardware surfaces. Owner-operators and marketing
 * live above the line; IT lives here.
 */
export const WIFI_HARDWARE_GROUPS: NavItem[] = [
  // SnapGuard — Stampede's own MikroTik offering (a device is linked).
  {
    id: 'wifi-snapguard',
    label: 'SnapGuard',
    icon: ShieldCheck,
    requiresVenue: true,
    separatorBefore: true,
    isVisible: (ctx) => ctx.hardwareTier === 'snapguard',
    // Parent lands on Health (first child); accordion auto-expands here.
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/wi-fi/snap-guard/health`,
    activeMatch: 'prefix',
    children: [
      {
        id: 'snap-health',
        label: 'Health',
        icon: HeartPulse,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/health`,
      },
      {
        id: 'snap-sessions',
        label: 'Sessions',
        icon: Users,
        requiresVenue: true,
        // Index route — exact match so it isn't "active" on every sub-tab.
        activeMatch: 'exact',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard`,
      },
      {
        id: 'snap-access-points',
        label: 'Devices',
        icon: Boxes,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/access-points`,
      },
      {
        id: 'snap-staff-comms',
        label: 'Staff comms',
        icon: Headphones,
        requiresVenue: true,
        // Beta: Stampede staff only for now. Fails closed while the user
        // query is still loading (userEmail undefined).
        badge: { label: 'Beta', tone: 'new' },
        isVisible: (ctx) =>
          ctx.userEmail?.toLowerCase().endsWith('@stampede.ai') ?? false,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/staff-comms`,
      },
      {
        id: 'snap-camera-wall',
        label: 'Camera wall',
        icon: LayoutGrid,
        requiresVenue: true,
        // Beta: Stampede staff only for now. Fails closed while the user
        // query is still loading (userEmail undefined).
        badge: { label: 'Beta', tone: 'new' },
        isVisible: (ctx) =>
          ctx.userEmail?.toLowerCase().endsWith('@stampede.ai') ?? false,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/camera-wall`,
      },
      {
        id: 'snap-networks',
        label: 'Networks',
        icon: Network,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/rate-limits`,
      },
      {
        id: 'snap-firewall',
        label: 'Firewall',
        icon: Flame,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/firewall`,
      },
      {
        id: 'snap-console',
        label: 'Console',
        icon: SquareTerminal,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/console`,
      },
      {
        id: 'snap-setup',
        label: 'Setup',
        icon: Wrench,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/snap-guard/setup`,
      },
    ],
  },

  // Hardware — bring-your-own (UniFi / plain MikroTik / click-through / not yet
  // configured). Children adapt to the sub-tier via each child's `isVisible`.
  {
    id: 'wifi-hardware',
    label: 'Hardware',
    icon: Cpu,
    requiresVenue: true,
    separatorBefore: true,
    isVisible: (ctx) => ctx.hardwareTier !== 'snapguard',
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi/hardware`,
    activeMatch: 'prefix',
    children: [
      {
        id: 'hw-overview',
        label: 'Overview',
        icon: Gauge,
        requiresVenue: true,
        // The UniFi Overview hosts controller credentials + site/SSID mapping
        // + Resync inline, so there's no separate Controller child (Phase 2
        // may split it out). Prefix of /hardware/setup + /hardware/connections
        // → exact-match so those don't light Overview.
        activeMatch: 'exact',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/hardware`,
      },
      {
        id: 'hw-access-points',
        label: 'Access Points',
        icon: Radio,
        requiresVenue: true,
        isVisible: (ctx) =>
          ctx.hardwareTier === 'unifi' ||
          ctx.hardwareTier === 'mikrotik' ||
          ctx.hardwareTier === 'meraki',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/access-points`,
      },
      {
        id: 'hw-ip-leases',
        label: 'IP Leases',
        icon: Network,
        requiresVenue: true,
        isVisible: (ctx) => ctx.hardwareTier === 'mikrotik',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/ip-addresses`,
      },
      {
        id: 'hw-firewall',
        label: 'Firewall',
        icon: Flame,
        requiresVenue: true,
        isVisible: (ctx) => ctx.hardwareTier === 'mikrotik',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/firewall`,
      },
      {
        id: 'hw-sessions',
        label: 'Sessions',
        icon: Users,
        requiresVenue: true,
        // UniFi (polled), plain MikroTik (RADIUS) and Meraki (cloud RADIUS
        // accounting) report real usage and session end — the full journey.
        isVisible: (ctx) =>
          ctx.hardwareTier === 'unifi' ||
          ctx.hardwareTier === 'mikrotik' ||
          ctx.hardwareTier === 'meraki',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/sessions`,
      },
      {
        id: 'hw-connections',
        label: 'Connections',
        icon: Link2,
        requiresVenue: true,
        // Click-through hardware has no usage/accounting data — a plain
        // auth-events list, clearly labelled as such.
        isVisible: (ctx) =>
          ctx.hardwareTier === 'click-through' ||
          ctx.hardwareTier === 'unconfigured',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/hardware/connections`,
      },
      {
        id: 'hw-setup',
        label: 'Setup',
        icon: Wrench,
        requiresVenue: true,
        // UniFi is configured inline on Overview/Controller — no wizard.
        isVisible: (ctx) => ctx.hardwareTier !== 'unifi',
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/hardware/setup`,
      },
    ],
  },
]
